import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { LoadingPaymentProcessing } from "@/components/ui/loading-payment-processing";
import { useToast } from "@/hooks/use-toast";
import { useCheckOrderStatus } from "@/hooks/api/query/useCheckOrderStatus";
import { queryKeys } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { RefreshCw, AlertTriangle } from "lucide-react";
import DevSimulatePurchaseButton from "@/components/debug/DevSimulatePurchaseButton";

const POLLING_TIMEOUT_MS = 30_000;

// Exponential backoff: 1s, 2s, 4s, 8s, then 5s intervals
function getPollingInterval(attemptCount: number): number {
  if (attemptCount < 4) return 1000 * 2 ** attemptCount;
  return 5000;
}

export default function PaymentProcessing() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [startTime, setStartTime] = useState<number | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [manualCheckLoading, setManualCheckLoading] = useState(false);

  const checkoutId = searchParams.get("checkout_id");
  const redirectParam = searchParams.get("redirect");
  const redirectUrl = redirectParam
    ? decodeURIComponent(redirectParam)
    : "/identificare-raportare";

  // Track when polling started
  useEffect(() => {
    if (checkoutId && !startTime) {
      setStartTime(Date.now());
    }
  }, [checkoutId, startTime]);

  const { data, isError, refetch } = useCheckOrderStatus(
    checkoutId || "",
    !!checkoutId && !timedOut,
    (query) => {
      const responseData = query.state.data;

      if (responseData?.is_completed) return false;

      // Check timeout
      if (startTime && Date.now() - startTime >= POLLING_TIMEOUT_MS) {
        setTimedOut(true);
        return false;
      }

      return getPollingInterval(query.state.dataUpdateCount);
    }
  );

  // Redirect if no checkout_id
  useEffect(() => {
    if (!checkoutId) {
      toast({
        title: "Eroare",
        description: "ID-ul sesiunii de plată lipsește",
        variant: "destructive",
      });
      navigate("/dashboard");
    }
  }, [checkoutId, navigate, toast]);

  // Handle success — invalidate credit queries so balance is fresh (F7)
  useEffect(() => {
    if (data?.is_completed && startTime) {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 3000 - elapsed);

      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.credits.all });
        toast({
          title: "Plată confirmată!",
          description: "Creditele au fost adăugate în cont",
        });
        navigate(redirectUrl);
      }, remaining);
    }
  }, [data, navigate, toast, startTime, redirectUrl, queryClient]);

  // Handle errors
  useEffect(() => {
    if (isError) {
      toast({
        title: "Eroare la verificarea plății",
        description: "A apărut o eroare. Te rugăm să încerci din nou.",
        variant: "destructive",
      });
      navigate("/dashboard");
    }
  }, [isError, navigate, toast]);

  // Manual re-check after timeout
  const handleManualCheck = useCallback(async () => {
    setManualCheckLoading(true);
    try {
      const result = await refetch();
      if (result.data?.is_completed) {
        setTimedOut(false); // will trigger the success effect
      } else {
        toast({
          title: "Încă în procesare",
          description:
            "Plata nu a fost confirmată încă. Încearcă din nou în câteva momente.",
        });
      }
    } finally {
      setManualCheckLoading(false);
    }
  }, [refetch, toast]);

  // Recovery UI when polling times out (F3)
  if (timedOut && !data?.is_completed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <DevSimulatePurchaseButton checkoutId={checkoutId} />
        <Card className="w-full max-w-md bg-muted/30 border-0 shadow-lg">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="p-4 rounded-full bg-yellow-500/10">
                <AlertTriangle className="h-8 w-8 text-yellow-600" />
              </div>
            </div>
            <div className="space-y-2">
              <h2 className="font-heading text-2xl font-semibold tracking-tight">
                Confirmarea durează mai mult decât de obicei
              </h2>
              <p className="text-sm text-muted-foreground">
                Plata ta a fost procesată, dar confirmarea creditelor întârzie.
                Acest lucru se rezolvă de obicei în câteva minute.
              </p>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={handleManualCheck}
              disabled={manualCheckLoading}
              className="w-full"
            >
              <RefreshCw
                className={`mr-2 h-4 w-4 ${manualCheckLoading ? "animate-spin" : ""}`}
              />
              {manualCheckLoading ? "Se verifică..." : "Verifică din nou"}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard")}
              className="w-full"
            >
              Mergi la Dashboard
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              Dacă problema persistă, contactează-ne la{" "}
              <a
                href="mailto:support@nis2assist.ro"
                className="underline text-primary"
              >
                support@nis2assist.ro
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <LoadingPaymentProcessing />
      <DevSimulatePurchaseButton checkoutId={checkoutId} />
    </>
  );
}
